import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { NgbDate, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { formatToNgbDate } from 'src/app/common/app.helpers';
import AppValues from 'src/app/common/app.values';
import FieldsConstants from 'src/app/common/fields.constants';
import { DependentDatePickerData, FieldAttribute } from 'src/app/interfaces/form-and-field.interface';

@Injectable()
export class DateValidationService {
  private readonly minDateMapper: {
    [key: string]: NgbDateStruct;
  } = {
    default: this.defaultMinDate(),
  };

  private readonly maxDateMapper: {
    [key: string]: NgbDateStruct;
  } = {
    date_of_birth: this.getTodayNgbDateStruct(),
    default: this.defaultMaxDate(),
  };

  public getMinDate(attribute: FieldAttribute): NgbDateStruct {
    return this.minDateMapper[attribute.backendAttribute.name] ?? this.defaultMinDate();
  }

  public getMaxDate(attribute: FieldAttribute): NgbDateStruct {
    return this.maxDateMapper[attribute.backendAttribute.name] ?? this.defaultMaxDate();
  }

  public notExistingDateValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value !== 'string' && !AppValues.datePattern.test(control.value as string)) {
        return null;
      }

      const date: NgbDateStruct = formatToNgbDate(control.value as string);
      const jsDate: Date = new Date(date.year, date.month - 1, date.day);

      if (isNaN(jsDate.getTime())) {
        return null;
      }

      const isDateExist: boolean =
        jsDate.getFullYear() === date.year && jsDate.getMonth() + 1 === date.month && jsDate.getDate() === date.day;

      return isDateExist ? null : { notExistingDate: true };
    };
  }

  public yearLessThenAllowed(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (typeof control.value !== 'string' && !AppValues.datePattern.test(control.value as string)) {
        return null;
      }

      const date: NgbDateStruct = formatToNgbDate(control.value as string);
      const isYearCorrect: boolean = date.year >= FieldsConstants.MIN_YEAR_ALLOW;

      return isYearCorrect ? null : { yearLessThenAllowed: true };
    };
  }

  public dateRangeValidator(validationField: string): ValidatorFn {
    enum Fields {
      startDate = 'start_date',
      endDate = 'end_date',
    }

    return (control: AbstractControl): ValidationErrors | null => {
      const fieldValue: (field: Fields) => string = (field: Fields) => control.parent.get(field).value as string;
      const isDate: (value: string | DependentDatePickerData) => boolean = (value: string | DependentDatePickerData) =>
        typeof value === 'string' && AppValues.datePattern.test(value);

      if (!control.parent) {
        return null;
      }

      if (!isDate(fieldValue(Fields.startDate)) || !isDate(fieldValue(Fields.endDate))) {
        return null;
      }

      const startDate: NgbDateStruct = formatToNgbDate(fieldValue(Fields.startDate));
      const endDate: NgbDateStruct = formatToNgbDate(fieldValue(Fields.endDate));

      if (validationField === Fields.startDate && !control.parent.get(Fields.endDate).hasError('endDate')) {
        return NgbDate.from(startDate).after(NgbDate.from(endDate)) ? { startDate: true } : null;
      }

      if (validationField === Fields.endDate && !control.parent.get(Fields.startDate).hasError('startDate')) {
        return NgbDate.from(endDate).before(NgbDate.from(startDate)) ? { endDate: true } : null;
      }

      return null;
    };
  }

  private getTodayNgbDateStruct(): NgbDateStruct {
    const currentDate: Date = new Date();

    return {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth() + 1,
      day: currentDate.getDate(),
    };
  }

  private defaultMinDate(): NgbDateStruct {
    return { year: FieldsConstants.MIN_YEAR_ALLOW, month: 1, day: 1 };
  }

  private defaultMaxDate(): NgbDateStruct {
    const todayNgbDateStruct: NgbDateStruct = this.getTodayNgbDateStruct();
    return { ...todayNgbDateStruct, year: todayNgbDateStruct.year + 100 };
  }
}
